import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@semsis/ui/base.css';
import './safe-area.css';
import { initPostHog, enableSessionReplay, hasConsent } from '@referee/analytics';
import { setupSentry } from './sentry-setup';
import './i18n';
import { App } from './App';
import { registerServiceWorker } from './utils/registerServiceWorker';
import { isNativePlatform } from './utils/platform';
import { getScreenshotScenario } from './screenshot/scenario';
// Imported as a namespace: the D-14 seed hook has exactly one call site
// below, reached only through this module reference.
import * as screenshotSeed from './screenshot/seed';

void setupSentry();
registerServiceWorker();

if (isNativePlatform()) {
  import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
    StatusBar.setBackgroundColor({ color: '#2a579c' });
    StatusBar.setStyle({ style: Style.Dark });
  });
}

async function start() {
  // D-10/D-14: the screenshot scenario is inert (null) on every real device
  // and on the web — see src/screenshot/scenario.ts. When it is set, seed
  // deterministic demo progress before the app ever renders.
  const scenario = getScreenshotScenario();
  if (scenario) {
    await screenshotSeed.applyScreenshotSeed(scenario);
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  if (!scenario) {
    // Initialize analytics immediately after render.
    //
    // This must NOT be deferred via requestIdleCallback: it has no deadline, and a
    // user who clicks a same-tab link before the browser reaches an idle slot
    // navigates away before the callback ever fires, so no session/identity is
    // ever established for that page view. See
    // .planning/debug/resolved/posthog-session-restart.md for the investigation.
    initPostHog();

    // Session replay attaches a DOM mutation observer and is comparatively
    // expensive to start. It can stay off the critical path: distinct_id/session
    // continuity (the bug above) is already established by initPostHog(), so a
    // deferred start here only risks missing the first few ms of a recording,
    // not the session itself. Bounded with a timeout so it still runs even if
    // the browser is never idle.
    if (hasConsent('sessionReplay')) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(enableSessionReplay, { timeout: 2000 });
      } else {
        setTimeout(enableSessionReplay, 0);
      }
    }
  }
}

start();
